<template>
  <div>
    <label v-if="label" :for="label" class="textarea-merge-tag-input__label">
      {{ label }}
    </label>
    <textarea
      :id="label"
      ref="editorRef"
      class="textarea-merge-tag-input__editor"
      :placeholder="placeholder"
    ></textarea>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import Tagify from '@yaireo/tagify';
import '@yaireo/tagify/dist/tagify.css';
import { parser } from './parser';

type SmsMergeTagInputProps = {
  modelValue: string;
  label?: string;
  placeholder?: string;
};
const props = defineProps<SmsMergeTagInputProps>();

const editorRef = ref<HTMLTextAreaElement | null>(null);
let tagify: Tagify<Tagify.TagData> | null = null;

const insertMergeTagAtCursor = (tag: string) => {
  if (!tagify) return;
  tagify.addTags([tag]);
  const value = tagify.getMixedTagsAsString();
  emitChange(value);
};

const setContent = (val: string) => {
  if (!editorRef.value) return;
  editorRef.value.value = parser().parse(val);
  emitChange(val);
};

onMounted(() => {
  if (editorRef.value) {
    editorRef.value.value = parser().parse(props.modelValue);

    tagify = new Tagify(editorRef.value, {
      mixTagsInterpolator: ['{{', '}}'],
      mode: 'mix',
      editTags: false,
      duplicates: true,
    });

    tagify.on('change', e => {
      const value = e.detail.tagify.getMixedTagsAsString();
      emitChange(value);
    });

    tagify.on('input', e => {
      const value = e.detail.tagify.getMixedTagsAsString();
      emitChange(value);
    });
  }
});

const emitChange = (mixedTag: string) => {
  const serialized = parser().serialize(mixedTag);
  emits('update:model-value', serialized);
};

type Emit = {
  'update:model-value': [value: string];
};
const emits = defineEmits<Emit>();

export type TextareaMergeTagInputExpose = {
  insertMergeTagAtCursor: (tag: string) => void;
  setContent: (val: string) => void;
};

defineExpose({
  insertMergeTagAtCursor,
  setContent,
});
</script>

<style>
@reference "tailwindcss";

.textarea-merge-tag-input__label {
  @apply block text-sm leading-5 font-medium text-gray-700 mb-1;
}

.textarea-merge-tag-input__editor {
  --tags-border-color: none;
  --tags-hover-border-color: none;

  --tag-bg: theme('colors.blue.100');
  --tag-hover: theme('colors.blue.100');
  --tag-text-color: theme('colors.blue.800');
  --tag-border-radius: theme('borderRadius.full');
  --tag-pad: 2px 4px;

  .tagify__input {
    @apply sm:text-sm border-gray-300 border-1 rounded-md px-3 py-2 shadow-none focus:border-indigo-500 max-h-48 min-h-16 sm:min-h-27 overflow-y-auto;
  }
}
</style>
